import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { usePortalContext } from "@/hooks/usePortalContext";
import { useTRPC } from "@/trpc/client";

/**
 * The two halves of the Comercios screen: the buildings the organization
 * already has, and the requests still moving through Palomma's queue. Both
 * come from queries the portal already exposes — `merchants.list` is the same
 * call the context switcher makes, narrowed here to this organization.
 */
export function useOrgMerchantRequests() {
  const trpc = useTRPC();
  const { context } = usePortalContext();
  const orgId = context?.type === "org" ? context.id : undefined;

  const requestsQuery = useQuery({
    ...trpc.merchantRequests.list.queryOptions({ rentalsOrgId: orgId ?? "" }),
    enabled: !!orgId,
  });

  const merchantsQuery = useQuery({
    ...trpc.merchants.list.queryOptions(undefined),
    staleTime: 60_000,
  });

  const merchants = useMemo(
    () => (merchantsQuery.data ?? []).filter((m) => m.orgId === orgId),
    [merchantsQuery.data, orgId]
  );

  return {
    orgId,
    merchants,
    requests: requestsQuery.data ?? [],
    isLoading: requestsQuery.isPending || merchantsQuery.isPending,
    isError: requestsQuery.isError || merchantsQuery.isError,
    errorCode:
      requestsQuery.error?.data?.code ?? merchantsQuery.error?.data?.code,
    refetch: () => {
      void requestsQuery.refetch();
      void merchantsQuery.refetch();
    },
  };
}
